/**
 * Oggi sul computer, ridisegnata il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: cosa devo fare oggi, e c'è qualcosa che brucia?
 * La forma: l'agenda della giornata a sinistra (prima le cose che non possono
 * aspettare, poi quelle di oggi e della settimana, sotto i soldi che escono ed
 * entrano nei prossimi 7 giorni coi loghi), e a destra tre aree con una frase
 * ciascuna: soldi, lavoro, vendite. In cima la risposta in parole.
 *
 * Ogni numero compare una volta sola: i "problemi gravi" di prima ripetevano
 * le righe dell'agenda (Run Factory tre volte, l'IVA due, la frase lunga due),
 * e qui restano solo quello che l'agenda non dice (il saldo sotto zero,
 * l'aggiornamento della banca che non va). Le pagine fuori menu (Fisco,
 * Situazione) non si aprono più da qui: si va al Calendario dei pagamenti o
 * ai Debiti col fisco.
 *
 * Nessun conto fatto qui sui soldi: arrivano già fatti da GET /admin/situazione
 * e GET /admin/oggi. Le vendite si contano sulle trattative aperte, le stesse
 * della pagina Trattative.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { euro, fmtDate } from '../../lib/utils';
import { canReadAdmin, canWriteAdmin } from '../admin/adminApi';
import { useAdminActions } from '../admin/useAdminActions';
import { ADMIN_PATH } from '../../lib/adminRoutes';
import { canSeeModule } from '../../lib/nav';
import { situazioneQuery, type Situazione } from '../admin/situazione/situazioneApi';
import { dealsListQuery, counterparty, daysFromToday, type DealItem } from '../deals/dealsApi';
import { leggibile } from '../../components/mobile/MobileKit';
import { ActionStream } from './ActionStream';
import { useActionStream } from './useActionStream';
import { oggiQuery, NATURA_LABEL, type OggiSummary, type OggiRigaPagamento, type OggiRigaFattura } from './oggiApi';
import { ModuleKey } from '@masamune/shared';
import './soldi.css';
import './oggi-desktop.css';

const GIORNI_SETTIMANA = 7;

export function OggiDesktop() {
  const user = useAuthStore((s) => s.user);
  const canAdmin = canReadAdmin(user);
  const canDeals = canSeeModule(user, ModuleKey.TRATTATIVE);
  const { counts, isLoading } = useActionStream();

  const { data } = useQuery({ ...oggiQuery(), enabled: canAdmin, retry: 1 });
  const { data: sit, isLoading: loadingSit } = useQuery({ ...situazioneQuery({ mesi: 3 }), enabled: canAdmin, retry: 1 });

  const oggi = data?.today ?? sit?.oggi ?? new Date().toISOString().slice(0, 10);
  const dataLunga = new Date(`${oggi}T00:00:00.000Z`).toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });

  const daFare = counts.critical + counts.today + counts.week;
  const risposta = isLoading && daFare === 0
    ? 'Sto mettendo in fila le cose da fare…'
    : daFare === 0
      ? 'Oggi non c’è niente da fare con urgenza.'
      : counts.critical > 0
        ? counts.critical === daFare
          ? <>Oggi ci {daFare === 1 ? 'è' : 'sono'} <strong>{daFare} {daFare === 1 ? 'cosa da fare' : 'cose da fare'}</strong> e <strong className="og2-brucia">{daFare === 1 ? 'non può aspettare' : 'nessuna può aspettare'}</strong>.</>
          : <>Oggi ci sono <strong>{daFare} cose da fare</strong> e <strong className="og2-brucia">{counts.critical} non {counts.critical === 1 ? 'può' : 'possono'} aspettare</strong>.</>
        : <>Oggi ci {daFare === 1 ? 'è' : 'sono'} <strong>{daFare} {daFare === 1 ? 'cosa da fare' : 'cose da fare'}</strong>, niente che bruci.</>;

  return (
    <div className="page og2">
      <header className="og2-hd">
        <div className="og2-hd-main">
          <SectionMark icon="dashboard" className="og2-sez" />
          <div className="min-w-0">
            <div className="og2-data">{dataLunga.charAt(0).toUpperCase() + dataLunga.slice(1)}</div>
            <h1 className="og2-title">Ciao, <strong>{user?.firstName}</strong>.</h1>
            <p className="og2-risposta">{risposta}</p>
          </div>
        </div>
      </header>

      <div className="og2-grid">
        {/* ── L'agenda ─────────────────────────────────────────────────── */}
        <section className="og2-agenda" aria-label="Agenda di oggi">
          <h2 className="og2-h2"><Icon name="checklist" size={15} /> Agenda di oggi</h2>
          <ActionStream />
          {canAdmin && data && <Settimana d={data} />}
        </section>

        {/* ── Le tre aree ──────────────────────────────────────────────── */}
        <aside className="og2-aree" aria-label="Soldi, lavoro e vendite">
          {canAdmin && (sit ? <AreaSoldi s={sit} /> : loadingSit ? <AreaVuota titolo="Soldi" icon="wallet" /> : (
            <Area icon="wallet" titolo="Soldi">
              <p className="og2-frase og2-mute">I numeri dei soldi non arrivano: l’API non risponde. Le cose da fare restano qui accanto.</p>
            </Area>
          ))}
          {canAdmin && (data ? <AreaLavoro d={data} /> : <AreaVuota titolo="Lavoro" icon="cube" />)}
          {canDeals && <AreaVendite />}
          {!canAdmin && !canDeals && (
            <p className="og2-frase og2-mute">I numeri di soldi e vendite sono riservati a chi ha l’Amministrazione o le Trattative.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

// ─── Pezzi comuni delle aree ─────────────────────────────────────────────────

function Area({ icon, titolo, link, children }: { icon: IconName; titolo: string; link?: { to: string; label: string }; children: React.ReactNode }) {
  return (
    <section className="og2-area">
      <div className="og2-area-hd">
        <span className="og2-area-ico"><Icon name={icon} size={16} /></span>
        <h2 className="og2-area-t">{titolo}</h2>
        {link && <Link to={link.to} className="og2-area-link">{link.label} <Icon name="chevronR" size={11} /></Link>}
      </div>
      {children}
    </section>
  );
}

function AreaVuota({ titolo, icon }: { titolo: string; icon: IconName }) {
  return (
    <Area icon={icon} titolo={titolo}>
      <div className="skeleton-col"><span className="skeleton sk-block" /><span className="skeleton sk-block" /></div>
    </Area>
  );
}

// ─── Soldi ───────────────────────────────────────────────────────────────────

/** Il nome del mese con l'anno solo se non è quello di oggi: "dicembre", "gennaio 2027". */
function nomeMese(m: { mese: string; nome: string }, annoOggi: number): string {
  const n = m.nome.toLowerCase();
  return Number(m.mese.slice(0, 4)) === annoOggi ? n : `${n} ${m.mese.slice(0, 4)}`;
}

function AreaSoldi({ s }: { s: Situazione }) {
  const user = useAuthStore((st) => st.user);
  const canWrite = canWriteAdmin(user);
  const { syncQonto, syncFic } = useAdminActions();
  const annoOggi = Number(s.oggi.slice(0, 4));
  const mesi = s.previsione.mesi;
  const basso = mesi.find((m) => m.mese === s.previsione.piuStretto) ?? null;
  const sottoZero = mesi.find((m) => m.mese === s.previsione.sottoZero) ?? null;
  const banca = s.disponibili.totale;
  // Dei problemi gravi resta solo quello che l'agenda non dice: l'aggiornamento
  // della banca o di FattureInCloud che non va (il mese sotto zero è la frase
  // qui sotto; tasse entro 7 giorni e fatture in ritardo sono già nell'agenda).
  const sync = s.problemi.filter((p) => p.tipo === 'SYNC');

  return (
    <Area icon="wallet" titolo="Soldi" link={{ to: ADMIN_PATH.calendario, label: 'Calendario dei pagamenti' }}>
      <p className="og2-frase">
        {banca < 0
          ? <>Il conto è <strong className="og2-neg">in rosso di {euro(-banca)}</strong></>
          : <>Sul conto ci sono <strong>{euro(banca)}</strong></>}
        <span className="og2-mute"> ({s.disponibili.bancaAggiornataTesto})</span>.{' '}
        {basso && (basso.saldoFine < 0
          ? <>Con le entrate e le uscite che conosciamo, il saldo {sottoZero && sottoZero.mese !== basso.mese ? <>va sotto zero a fine {nomeMese(sottoZero, annoOggi)} e </> : null}scende fino a <strong className="og2-neg">{euro(basso.saldoFine)}</strong> a fine {nomeMese(basso, annoOggi)}.</>
          : <>Con le entrate e le uscite che conosciamo, il punto più basso è a fine {nomeMese(basso, annoOggi)}, a <strong>{euro(basso.saldoFine)}</strong>.</>)}
      </p>

      {mesi.length > 0 && (
        <SaldoMesi
          punti={[
            { k: 'oggi', label: 'oggi', tip: 'Oggi sul conto', v: banca },
            ...mesi.map((m) => ({ k: m.mese, label: m.nome.slice(0, 3).toLowerCase(), tip: `Saldo previsto a fine ${nomeMese(m, annoOggi)}`, v: m.saldoFine })),
          ]}
        />
      )}

      {s.scaduti.totale > 0 && (
        <Link to={ADMIN_PATH.debiti} className="og2-avviso">
          <Icon name="alert" size={14} className="og2-avviso-ico" />
          <span>
            In più ci sono <strong>{euro(s.scaduti.totale)}</strong> di pagamenti già scaduti e non fatti: {s.scaduti.descrizione}.
          </span>
          <Icon name="chevronR" size={12} className="og2-mute" />
        </Link>
      )}

      {sync.map((p, i) => {
        const azione = p.provider === 'QONTO' ? syncQonto : syncFic;
        return (
          <div key={`${p.tipo}-${i}`} className="og2-avviso is-sync">
            <Icon name="refresh" size={14} className="og2-avviso-ico" />
            <span>{p.testo}{p.dettaglio ? `: ${p.dettaglio}` : ''}. I numeri qui sopra potrebbero essere vecchi.</span>
            {canWrite && (
              <button type="button" className="btn btn-sm" disabled={azione.isPending} onClick={() => azione.mutate()}>
                {azione.isPending ? 'Aggiorno…' : 'Riprova'}
              </button>
            )}
          </div>
        );
      })}
    </Area>
  );
}

/**
 * Il saldo di oggi e quello previsto a fine di ogni mese, come colonne che
 * salgono o scendono dalla linea dello zero. Una serie sola: niente legenda, il
 * titolo lo dice la frase sopra. Il rosso tenue è lo stato "sotto zero", e la
 * cifra esatta sta nel suggerimento sopra la colonna.
 */
function SaldoMesi({ punti }: { punti: Array<{ k: string; label: string; tip: string; v: number }> }) {
  const H = 72;
  const maxPos = Math.max(0, ...punti.map((p) => p.v));
  const maxNeg = Math.max(0, ...punti.map((p) => -p.v));
  const scala = maxPos + maxNeg || 1;
  // La linea dello zero: in fondo se tutto è positivo, in cima se tutto è negativo.
  const zeroY = (maxPos / scala) * H;
  const descr = punti.map((p) => `${p.tip}: ${euro(p.v)}`).join('; ');
  return (
    <div className="og2-chart" role="img" aria-label={descr}>
      <div className="og2-chart-area" style={{ height: H }}>
        <span className="og2-chart-zero" style={{ top: zeroY }} aria-hidden="true" />
        {punti.map((p) => {
          const h = Math.max(2, (Math.abs(p.v) / scala) * H);
          const neg = p.v < 0;
          return (
            <span key={p.k} className="og2-col" data-tip={`${p.tip}: ${euro(p.v)}`}>
              <span
                className={`og2-bar${neg ? ' is-neg' : ''}`}
                style={neg ? { top: zeroY, height: h } : { top: zeroY - h, height: h }}
              />
            </span>
          );
        })}
      </div>
      <div className="og2-chart-x" aria-hidden="true">
        {punti.map((p) => <span key={p.k}>{p.label}</span>)}
      </div>
    </div>
  );
}

// ─── Lavoro ──────────────────────────────────────────────────────────────────

function AreaLavoro({ d }: { d: OggiSummary }) {
  const [conto, setConto] = useState(false);
  const l = d.lavoro.inMano;
  const fino = new Date(`${l.ricorrente.fino}T00:00:00.000Z`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const parti: React.ReactNode[] = [];
  if (l.ricorrente.valore > 0) parti.push(<>i canoni dei clienti fino a {fino}</>);
  if (l.progetti.valore > 0) parti.push(<>{euro(l.progetti.valore)} di progetti ancora da fatturare</>);
  if (l.trattativeVinte.valore > 0) parti.push(<>{euro(l.trattativeVinte.valore)} di trattative vinte</>);

  return (
    <Area icon="cube" titolo="Lavoro" link={{ to: '/progetti', label: 'Progetti' }}>
      <p className="og2-frase">
        {l.totale > 0
          ? <>Abbiamo in mano <strong>{euro(l.totale)}</strong> di lavoro da fatturare: {parti.map((p, i) => <span key={i}>{i > 0 && (i === parti.length - 1 ? ' e ' : ', ')}{p}</span>)}.</>
          : <>Non c’è lavoro firmato da fatturare: nessun canone, progetto o trattativa vinta.</>}
      </p>
      <button type="button" className="og2-toggle" aria-expanded={conto} onClick={() => setConto((v) => !v)}>
        <Icon name={conto ? 'chevronU' : 'chevronD'} size={12} /> {conto ? 'Nascondi come è fatto il conto' : 'Come è fatto il conto, senza contare due volte'}
      </button>
      {conto && <ComeEFattoIlConto d={d} />}
    </Area>
  );
}

/**
 * Le voci del lavoro in mano e quello che è stato tolto per non contare lo
 * stesso euro due volte, con la regola che lo dice. Era sempre aperta in cima
 * alla pagina; adesso si apre a richiesta dentro l'area Lavoro.
 */
function ComeEFattoIlConto({ d }: { d: OggiSummary }) {
  const c = d.controllo;
  const l = d.lavoro;
  return (
    <div className="og2-conto">
      <table className="og-proof">
        <tbody>
          {c.voci.map((v) => (
            <tr key={v.chiave}>
              <td>{v.etichetta}<em className="og-tag">{NATURA_LABEL[v.natura]}</em></td>
              <td className="n">{euro(v.valore)}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>Totale, senza doppioni</td>
            <td className="n">{euro(c.totaleNetto)}</td>
          </tr>
        </tbody>
      </table>
      <div className="og2-conto-t">Tolto perché sarebbe stato contato due volte</div>
      <table className="og-proof">
        <tbody>
          {c.scartato.map((s) => (
            <tr key={s.chiave}>
              <td>
                {s.etichetta} {s.count > 0 && <span className="mute-2">({s.count})</span>}
                <span className="og-why">{s.regola}</span>
              </td>
              <td className="n">−{euro(s.valore)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {l.inMano.progettiACanone.count > 0 && (
        <p className="og2-nota">
          {l.inMano.progettiACanone.count === 1 ? 'Un progetto si fattura' : `${l.inMano.progettiACanone.count} progetti si fatturano`} a rate ({euro(l.inMano.progettiACanone.valore)}):
          le rate sono già fra i canoni dei clienti, quindi non si sommano di nuovo.
        </p>
      )}
      {l.inMano.ricorrente.aggregati.valore > 0 && (
        <p className="og2-nota">
          {euro(l.inMano.ricorrente.aggregati.valore)} dei canoni sono voci senza cliente ({l.inMano.ricorrente.aggregati.count} incassi: GoDeal, Stripe, hosting):
          lì il controllo dei doppioni non può scattare, perché confronta il cliente.
        </p>
      )}
      {l.inVendita.rinnovi.count > 0 && (
        <p className="og2-nota">
          I rinnovi in trattativa ({l.inVendita.rinnovi.count}) non sono nel totale: il canone che rinnovano è già fra i canoni dei clienti.
        </p>
      )}
      <p className="og2-nota">
        {c.quadra
          ? `Sommando alla cieca farebbe ${euro(c.totaleLordo)}: ${euro(c.differenza)} di troppo, esattamente quanto è stato tolto.`
          : `Attenzione: sommando alla cieca farebbe ${euro(c.totaleLordo)}, e la differenza non coincide con quanto è stato tolto.`}
      </p>
    </div>
  );
}

// ─── Vendite ─────────────────────────────────────────────────────────────────

function AreaVendite() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ ...dealsListQuery({ view: 'aperte', sort: 'probabilita', page: 1, pageSize: 100 }), retry: 1 });

  const r = useMemo(() => {
    const items = data?.items ?? [];
    const valore = items.reduce((s, x) => s + x.value, 0);
    const pesato = items.reduce((s, x) => s + x.weighted, 0);
    const dataPassata = items.filter((x) => { const g = daysFromToday(x.expectedCloseDate); return g != null && g < 0; }).length;
    const senzaData = items.filter((x) => !x.expectedCloseDate).length;
    const azioniOggi = items.filter((x) => x.nextTask && (x.nextTask.isOverdue || daysFromToday(x.nextTask.dueDate) === 0)).length;
    return { items, valore, pesato, dataPassata, senzaData, azioniOggi, avanti: items.slice(0, 3) };
  }, [data]);

  if (isLoading && !data) return <AreaVuota titolo="Vendite" icon="handshake" />;
  const n = data?.total ?? r.items.length;

  return (
    <Area icon="handshake" titolo="Vendite" link={{ to: '/trattative', label: 'Trattative' }}>
      <p className="og2-frase">
        {n === 0
          ? <>Nessuna trattativa aperta.</>
          : <>
              <strong>{n} {n === 1 ? 'trattativa aperta vale' : 'trattative aperte valgono'} {euro(r.valore)}</strong>
              <span className="og2-mute">; pesate sulla probabilità di chiuderle, {euro(r.pesato)}</span>.
              {r.azioniOggi > 0 && <> Oggi {r.azioniOggi === 1 ? 'c’è da sentire un cliente' : `ci sono da sentire ${r.azioniOggi} clienti`}.</>}
            </>}
      </p>

      {r.avanti.length > 0 && (
        <div className="og2-deals">
          <div className="og2-deals-t">Le più vicine alla firma</div>
          {r.avanti.map((x) => <RigaDeal key={x.id} x={x} onClick={() => navigate(`/trattative/${x.id}`)} />)}
        </div>
      )}

      {(r.dataPassata > 0 || r.senzaData > 0) && (
        <Link to={r.dataPassata > 0 ? "/trattative?chiusura=passata" : "/trattative?solo=senza-data"} className="og2-avviso is-dato">
          <Icon name="calendarClock" size={14} className="og2-avviso-ico" />
          <span>
            {r.dataPassata > 0 && <>{r.dataPassata === 1 ? 'Una trattativa ha' : `${r.dataPassata} trattative hanno`} la data di chiusura prevista già passata: {r.dataPassata === 1 ? 'va aggiornata' : 'vanno aggiornate'}.</>}
            {r.dataPassata > 0 && r.senzaData > 0 && ' '}
            {r.senzaData > 0 && <>{r.senzaData === 1 ? 'Una non ha' : `${r.senzaData} non hanno`} ancora una data.</>}
          </span>
          <Icon name="chevronR" size={12} className="og2-mute" />
        </Link>
      )}
    </Area>
  );
}

function RigaDeal({ x, onClick }: { x: DealItem; onClick: () => void }) {
  const nome = leggibile(counterparty(x));
  return (
    <button type="button" className="og2-deal" onClick={onClick}>
      <BrandLogo name={nome} size={28} />
      <span className="og2-deal-tx">
        <span className="t">{nome}</span>
        <span className="s">{x.stage.name}</span>
      </span>
      <span className="og2-deal-v">{x.value > 0 ? euro(x.value) : <span className="og2-mute">senza valore</span>}</span>
    </button>
  );
}

// ─── La settimana dei soldi ──────────────────────────────────────────────────

/** Chi paga o chi incassa: il logo se è un servizio noto, un'icona per tasse e stipendi. */
function Segno({ v }: { v: OggiRigaPagamento }) {
  if (v.fonte === 'TASSE') return <span className="og2-segno"><Icon name="landmark" size={14} /></span>;
  if (v.fonte === 'STIPENDIO') return <span className="og2-segno"><Icon name="user" size={14} /></span>;
  return <BrandLogo name={v.controparte} size={28} />;
}

/**
 * I soldi che escono ed entrano nei prossimi 7 giorni, giorno per giorno, coi
 * loghi di chi li prende. È l'agenda dei soldi: quello che succede da solo
 * (gli addebiti), da sapere prima che succeda. Il conto lungo, mese per mese,
 * sta nel Calendario dei pagamenti.
 */
function Settimana({ d }: { d: OggiSummary }) {
  const giorni = useMemo(() => {
    type Voce = { k: string; dir: 'out' | 'in'; nome: string; sub: string; importo: number; v?: OggiRigaPagamento; f?: OggiRigaFattura };
    const m = new Map<string, Voce[]>();
    const entro = (iso: string | null) => { const g = daysFromToday(iso); return g != null && g >= 0 && g < GIORNI_SETTIMANA; };
    const add = (iso: string, x: Voce) => m.set(iso, [...(m.get(iso) ?? []), x]);
    for (const v of d.daPagare.voci) {
      if (!v.dueDate || !entro(v.dueDate)) continue;
      const sub = v.descrizione && v.descrizione !== v.controparte ? v.descrizione : v.fonte === 'TASSE' ? 'Tassa' : v.fonte === 'STIPENDIO' ? 'Stipendio' : v.fonte === 'FORNITORE' ? 'Fattura di un fornitore' : 'Spesa fissa';
      add(v.dueDate.slice(0, 10), { k: `${v.fonte}-${v.id}`, dir: 'out', nome: v.controparte, sub, importo: v.importo, v });
    }
    for (const f of d.fatture.prossime.voci) {
      if (!f.dueDate || !entro(f.dueDate)) continue;
      add(f.dueDate.slice(0, 10), { k: `in-${f.paymentId}`, dir: 'in', nome: f.cliente, sub: `Fattura ${f.numero ?? 'senza numero'}${f.rata ? `, rata ${f.rata.seq} di ${f.rata.su}` : ''}`, importo: f.importo, f });
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [d]);

  const esce = giorni.reduce((s, [, vs]) => s + vs.filter((x) => x.dir === 'out').reduce((a, x) => a + x.importo, 0), 0);
  const entra = giorni.reduce((s, [, vs]) => s + vs.filter((x) => x.dir === 'in').reduce((a, x) => a + x.importo, 0), 0);

  return (
    <div className="og2-sett">
      <h2 className="og2-h2">
        <Icon name="calendar" size={15} /> I soldi dei prossimi 7 giorni
        <span className="og2-h2-sub">
          {giorni.length === 0 ? 'niente in entrata né in uscita' : <>escono {euro(esce)}{entra > 0 ? <>, entrano {euro(entra)}</> : ', non entra niente'}</>}
        </span>
      </h2>
      {giorni.map(([iso, voci]) => {
        const g = daysFromToday(iso);
        const dt = new Date(`${iso}T00:00:00.000Z`);
        return (
          <div key={iso} className="og2-giorno">
            <div className="og2-giorno-data" title={fmtDate(iso)}>
              <span className="n">{dt.getUTCDate()}</span>
              <span className="m">{g === 0 ? 'oggi' : g === 1 ? 'domani' : dt.toLocaleDateString('it-IT', { weekday: 'short', timeZone: 'UTC' })}</span>
            </div>
            <div className="og2-giorno-voci">
              {voci.map((x) => (
                <div key={x.k} className="og2-soldo">
                  {x.v ? <Segno v={x.v} /> : <BrandLogo name={leggibile(x.nome)} size={28} />}
                  <span className="og2-soldo-tx">
                    <span className="t">{leggibile(x.nome)}</span>
                    <span className="s">{x.sub}</span>
                  </span>
                  <span className={`og2-soldo-v${x.dir === 'in' ? ' is-in' : ''}`}>{x.dir === 'in' ? '+' : '−'}{euro(x.importo)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
